import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import api from '../components/api';

const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [step, setStep] = useState('check'); // 'check' | 'sending' | 'sent'

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email.trim()) {
            setError("L'adresse email est requise");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError("Veuillez entrer une adresse email valide");
            return;
        }

        setLoading(true);

        // Étape 1 : vérifier que l'email existe
        try {
            await api.post('utilisateur/password-reset/check-email/', { email });
        } catch (err) {
            const msg = err.response?.data?.error || "Aucun compte trouvé avec cette adresse email.";
            setError(msg);
            setLoading(false);
            return;
        }

        // Étape 2 : envoyer l'OTP
        setStep('sending');
        try {
            await api.post('utilisateur/password-reset/send-otp/', { email });
            setStep('sent');
            setTimeout(() => {
                navigate('/verification-otp', { state: { email } });
            }, 1500);
        } catch (err) {
            const msg = err.response?.data?.error || "Impossible d'envoyer le code. Veuillez réessayer.";
            setError(msg);
            setStep('check');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-green-50 py-8 px-4">
            <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl">
                <div className="mb-6 text-center">
                    <img className="mx-auto h-14 w-auto" src="/logo-anpe.png" alt="ANPE Logo" />
                    <h2 className="mt-4 text-2xl font-bold text-gray-900">Mot de passe oublié</h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Entrez votre adresse email pour recevoir un code de vérification
                    </p>
                </div>

                {step === 'sent' ? (
                    <div className="text-center py-6">
                        <div className="flex justify-center mb-4">
                            <CheckCircle className="h-16 w-16 text-green-500" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Code envoyé !</h3>
                        <p className="text-gray-600 text-sm">
                            Un code de vérification a été envoyé à <strong>{email}</strong>
                        </p>
                        <p className="text-gray-500 text-xs mt-2">Redirection en cours...</p>
                    </div>
                ) : (
                    <>
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
                                <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                    Adresse email <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="email"
                                        type="email"
                                        autoComplete="email"
                                        className="appearance-none block w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                                        placeholder="exemple@email.com"
                                        value={email}
                                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                                    loading ? 'bg-orange-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700'
                                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-sm transition`}
                            >
                                {loading ? (
                                    <span className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {step === 'sending' ? 'Envoi du code...' : 'Vérification...'}
                                    </span>
                                ) : 'Envoyer le code'}
                            </button>
                        </form>
                    </>
                )}

                <div className="mt-6 text-center">
                    <Link to="/connexion" className="inline-flex items-center text-sm text-orange-600 hover:text-orange-500 font-medium">
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Retour à la connexion
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
